/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/require-await */
import { Subscription } from "rxjs";
import { DataChannel, DataChannelEvents, DataChannelStatus, LogEntry, RBEvent } from "../../models";
import { Service } from "../service";
import { LoggerService } from "../core";
import { PromiseQueue } from "../../utils/promiseQueue";
import { DataChannelService_RB, DataChannelServiceEvents } from "../dataChannel/dataChannel.service";
import { JsonRPCRequest, JsonRPCResponse } from "./jsonRPC.service";
import { stringifyError } from "../../utils/stringifyError";

export const TELEMETRY_SVC = 'TelemetryService';

export class TelemetryService extends Service {
    public static getInstance() { return Service.create(TELEMETRY_SVC, TelemetryService); }
    public static build() { return new TelemetryService(); }
    private constructor() { super(); }
    private supervisedServices: Record<string, any> = {};

    private loggerService = LoggerService.getInstance();
    private dataChannelService = DataChannelService_RB.getInstance();
    private subscription: Subscription = new Subscription();
    private dataChannelSubscription?: Subscription;
    private promiseQueue = PromiseQueue.create();
    private dataChannel?: DataChannel;

    public addSupervisedService(name: string, service: Service) {
        this.supervisedServices[name] = service;
    }

    public async start(): Promise<void> {
        if (!config.telemetry) return;

        this.loggerService.info(`[TelemetryService] start ()`);
        // Register supervisedServices

        this.subscription.add(this.loggerService.events.subscribe((logEntry: LogEntry) => {
            this.promiseQueue.add(() => this.handleLogEntry(logEntry));
        }));

        this.subscription.add(this.dataChannelService.subscribe(async (event: RBEvent<DataChannelServiceEvents>) => {
            this.dataChannel = event.data;
            if (event.name === DataChannelServiceEvents.ON_DATACHANNEL_AVAILABLE) {
                if (this.dataChannel.id === "telemetry") {
                    this.loggerService.info(`[TelemetryService] Connection request from (${this.dataChannel.publisher.dbId})`);
                    await this.dataChannel.accept();
                    this.subscribeToDataChannelEvents(this.dataChannel);
                }
            }
            if (event.name === DataChannelServiceEvents.ON_DATACHANNEL_CLOSED) {
                this.dataChannelSubscription?.unsubscribe();
            }
        },));
    }

    public async stop(): Promise<void> {
        this.loggerService.info(`[TelemetryService] stop ()`);
        this.subscription.unsubscribe();
        this.dataChannelSubscription?.unsubscribe();
    }

    public async handleLogEntry(logEntry: LogEntry): Promise<void> {
        const tasks = Object.values(this.supervisedServices).map(service => service.parseLogEntry(logEntry, this.dataChannel));
        await Promise.all(tasks);
    }

    private subscribeToDataChannelEvents(dataChannel: DataChannel): void {
        this.dataChannelSubscription = dataChannel.subscribe(
            (event) => {
                switch (event.name) {
                    case DataChannelEvents.ON_STATUS_CHANGED:
                        const status = event.data as DataChannelStatus;
                        if (status === DataChannelStatus.CONNECTED) {
                            this.loggerService.info(`[TelemetryService] Connection established with "${this.dataChannel.publisher.dbId}"`);
                        }
                        if (status === DataChannelStatus.CLOSED || status === DataChannelStatus.DISCONNECTED) {
                            this.dataChannelSubscription.unsubscribe();
                            this.loggerService.info(`[TelemetryService] Connection closed with "${this.dataChannel.publisher.dbId}" (status: ${status})`);
                        }
                        break;
                    case DataChannelEvents.ON_MESSAGE_RECEIVED:
                        this.handleRPCmessage(JsonRPCRequest.createFromJSONString(event.data));
                        break;
                }
            },
            [
                DataChannelEvents.ON_STATUS_CHANGED,
                DataChannelEvents.ON_MESSAGE_RECEIVED
            ]
        );
    }

    private async handleRPCmessage(request: JsonRPCRequest): Promise<void> {
        try {
            this.loggerService.info(`[TelemetryService] handleRPCmessage (${request?.method})`);
            const supervisedService = this.supervisedServices[request.service];
            const actionMethod = (supervisedService)[request.action];
            if (actionMethod) {
                const response = await actionMethod.bind(supervisedService)(request.params);
                const jsonRPCResponse = new JsonRPCResponse(response, request.id);
                this.dataChannel.sendMessage(jsonRPCResponse.json());
            }
            else this.loggerService.error(`[TelemetryService] handleRPCmessage (${request?.method}) -- method not available`);
        }
        catch (error) {
            this.loggerService.error(`[TelemetryService] handleRPCmessage() -- ${stringifyError(error)}`);
        }
    }

}
